import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, Mail, Phone, Building2, Trash2, 
  Edit, Copy, Tag, Calendar, Clock, UserCheck, Target,
  FileText, Activity, DollarSign, Package, Wrench, Plus, X, Check,
  MapPin, CreditCard, Star, Receipt, RefreshCw, Lock, Send
} from 'lucide-react';
import { AddProductModal, AddServiceModal, LocationModal, AddCardModal } from '../components/ClientModals';
import { CreateInvoiceModal } from '../components/CreateInvoiceModal';
import { ClientCarryoverOverview } from '../components/ClientCarryoverOverview';
import { ClientActivityTimeline } from '../components/ClientActivityTimeline';
import { ClientDocumentsPanel } from '../components/ClientDocumentsPanel';
import { EmailComposerModal } from '../components/EmailComposerModal';

function ClientDetailPage() {
  var params = useParams();
  var navigate = useNavigate();
  var authContext = useAuth();
  var api = authContext.api;
  
  var stateHooks = {
    client: useState(null),
    clientDraft: useState(null),
    loading: useState(true),
    activeTab: useState('overview'),
    notes: useState([]),
    activities: useState([]),
    isEditingClient: useState(false),
    clientProducts: useState([]),
    clientServices: useState([]),
    availableProducts: useState([]),
    availableServices: useState([]),
    showAddProductModal: useState(false),
    showAddServiceModal: useState(false),
    locations: useState([]),
    showLocationModal: useState(false),
    editLocation: useState(null),
    creditCards: useState([]),
    showAddCardModal: useState(false),
    invoices: useState([]),
    clientSubscriptions: useState([]),
    clientQuotes: useState([]),
    leadSales: useState([]),
    showCreateInvoiceModal: useState(false),
    subscriptionActionLoading: useState({}),
    showEmailModal: useState(false)
  };

  // Invoice editing state
  var editInvoiceRef = useState(null);
  var editInvoice = editInvoiceRef[0];
  var setEditInvoice = editInvoiceRef[1];
  var savingInvoiceRef = useState(false);
  var savingInvoice = savingInvoiceRef[0];
  var setSavingInvoice = savingInvoiceRef[1];

  // Recurring subscription inline edit state
  var editSubRef = useState(null);
  var editSubState = editSubRef[0];
  var setEditSub = editSubRef[1];

  var client = stateHooks.client[0];
  var setClient = stateHooks.client[1];
  var clientDraft = stateHooks.clientDraft[0];
  var setClientDraft = stateHooks.clientDraft[1];
  var loading = stateHooks.loading[0];
  var setLoading = stateHooks.loading[1];
  var activeTab = stateHooks.activeTab[0];
  var setActiveTab = stateHooks.activeTab[1];
  var notes = stateHooks.notes[0];
  var setNotes = stateHooks.notes[1];
  var activities = stateHooks.activities[0];
  var setActivities = stateHooks.activities[1];
  var isEditingClient = stateHooks.isEditingClient[0];
  var setIsEditingClient = stateHooks.isEditingClient[1];
  var clientProducts = stateHooks.clientProducts[0];
  var setClientProducts = stateHooks.clientProducts[1];
  var clientServices = stateHooks.clientServices[0];
  var setClientServices = stateHooks.clientServices[1];
  var availableProducts = stateHooks.availableProducts[0];
  var setAvailableProducts = stateHooks.availableProducts[1];
  var availableServices = stateHooks.availableServices[0];
  var setAvailableServices = stateHooks.availableServices[1];
  var showAddProductModal = stateHooks.showAddProductModal[0];
  var setShowAddProductModal = stateHooks.showAddProductModal[1];
  var showAddServiceModal = stateHooks.showAddServiceModal[0];
  var setShowAddServiceModal = stateHooks.showAddServiceModal[1];
  var locations = stateHooks.locations[0];
  var setLocations = stateHooks.locations[1];
  var showLocationModal = stateHooks.showLocationModal[0];
  var setShowLocationModal = stateHooks.showLocationModal[1];
  var editLocation = stateHooks.editLocation[0];
  var setEditLocation = stateHooks.editLocation[1];
  var creditCards = stateHooks.creditCards[0];
  var setCreditCards = stateHooks.creditCards[1];
  var showAddCardModal = stateHooks.showAddCardModal[0];
  var setShowAddCardModal = stateHooks.showAddCardModal[1];
  var invoices = stateHooks.invoices[0];
  var setInvoices = stateHooks.invoices[1];
  var clientSubscriptions = stateHooks.clientSubscriptions[0];
  var setClientSubscriptions = stateHooks.clientSubscriptions[1];
  var clientQuotes = stateHooks.clientQuotes[0];
  var setClientQuotes = stateHooks.clientQuotes[1];
  var leadSales = stateHooks.leadSales[0];
  var setLeadSales = stateHooks.leadSales[1];
  var showCreateInvoiceModal = stateHooks.showCreateInvoiceModal[0];
  var setShowCreateInvoiceModal = stateHooks.showCreateInvoiceModal[1];
  var subscriptionActionLoading = stateHooks.subscriptionActionLoading[0];
  var setSubscriptionActionLoading = stateHooks.subscriptionActionLoading[1];
  var showEmailModal = stateHooks.showEmailModal[0];
  var setShowEmailModal = stateHooks.showEmailModal[1];
  
  // Admin set portal password state
  var portalPasswordRef = useState('');
  var portalPassword = portalPasswordRef[0];
  var setPortalPassword = portalPasswordRef[1];
  var settingPortalPwRef = useState(false);
  var settingPortalPw = settingPortalPwRef[0];
  var setSettingPortalPw = settingPortalPwRef[1];

  useEffect(function() {
    loadClient();
    loadAvailableItems();
    loadLocations();
    loadCreditCards();
    loadInvoices();
    loadClientSubscriptions();
    loadClientQuotes();
    loadClientActivity();
  }, [params.id]);

  function loadClient() {
    setLoading(true);
    api.get('/api/clients/' + params.id).then(function(res) {
      setClient(res.data.client);
      setClientDraft(res.data.client);
      setNotes(res.data.client.notes ? [res.data.client.notes] : []);
      setClientProducts(res.data.client.products || []);
      setClientServices(res.data.client.services || []);
      setLoading(false);
    }).catch(function() {
      toast.error('Client not found');
      navigate('/crm/clients');
    });
  }

  function loadAvailableItems() {
    api.get('/api/billing/products').then(function(res) {
      setAvailableProducts(res.data.products || []);
    });
    api.get('/api/billing/services').then(function(res) {
      setAvailableServices(res.data.services || []);
    });
    api.get('/api/billing/lead-sales').then(function(res) {
      setLeadSales(res.data.lead_sales || []);
    }).catch(function() {});
  }

  function loadLocations() {
    api.get('/api/clients/' + params.id + '/locations').then(function(res) {
      setLocations(res.data.locations || []);
    });
  }

  function loadCreditCards() {
    api.get('/api/clients/' + params.id + '/cards').then(function(res) {
      setCreditCards(res.data.cards || []);
    }).catch(function() {
      setCreditCards([]);
    });
  }

  function loadInvoices() {
    api.get('/api/clients/' + params.id + '/invoices').then(function(res) {
      setInvoices(res.data.invoices || []);
    }).catch(function() {
      setInvoices([]);
    });
  }

  function loadClientQuotes() {
    api.get('/api/clients/' + params.id + '/quotes').then(function(res) {
      setClientQuotes(res.data.quotes || []);
    }).catch(function() {});
  }

  function loadClientSubscriptions() {
    api.get('/api/subscriptions/client/' + params.id).then(function(res) {
      setClientSubscriptions(res.data.subscriptions || []);
    }).catch(function() {
      setClientSubscriptions([]);
    });
  }

  function loadClientActivity() {
    api.get('/api/clients/' + params.id + '/activity').then(function(res) {
      setActivities(res.data || []);
    }).catch(function() {
      setActivities([]);
    });
  }

  function buildClientUpdatePayload() {
    if (!clientDraft) return {};
    var payload = {};
    var readOnlyKeys = {
      id: true,
      original_lead_id: true,
      created_at: true,
      updated_at: true,
      converted_at: true,
      products: true,
      services: true
    };
    for (var key in clientDraft) {
      if (!readOnlyKeys[key]) {
        payload[key] = clientDraft[key];
      }
    }
    return payload;
  }

  function handleUnlockClientEditing() {
    setClientDraft(client);
    setIsEditingClient(true);
  }

  function handleCancelClientEditing() {
    setClientDraft(client);
    setIsEditingClient(false);
  }

  function handleSaveClientEditing() {
    api.put('/api/clients/' + params.id, buildClientUpdatePayload()).then(function() {
      toast.success('Client updated');
      setIsEditingClient(false);
      loadClient();
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to update client');
    });
  }

  function formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  }

  function handleGenerateSubscriptionInvoice(subscriptionId) {
    setSubscriptionActionLoading(function(prev) {
      return { ...prev, [subscriptionId]: 'invoice' };
    });

    api.post('/api/subscriptions/' + subscriptionId + '/generate-invoice').then(function(res) {
      toast.success('Invoice ' + res.data.invoice_number + ' created');
      loadInvoices();
      loadClientSubscriptions();
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to create recurring invoice');
    }).finally(function() {
      setSubscriptionActionLoading(function(prev) {
        return { ...prev, [subscriptionId]: null };
      });
    });
  }

  function handleChargeSubscriptionNow(subscriptionId) {
    setSubscriptionActionLoading(function(prev) {
      return { ...prev, [subscriptionId]: 'charge' };
    });

    api.post('/api/payments/subscriptions/' + subscriptionId + '/charge-now', {
      origin_url: window.location.origin,
      payment_method: 'cc',
      created_by: 'client_detail_page'
    }).then(function(res) {
      if (res.data.mode === 'checkout' && res.data.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.success('Subscription charged successfully');
      loadInvoices();
      loadClientSubscriptions();
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to charge subscription');
    }).finally(function() {
      setSubscriptionActionLoading(function(prev) {
        return { ...prev, [subscriptionId]: null };
      });
    });
  }

  function handleSaveSubscriptionEdit(subId) {
    if (!editSubState) return;
    api.put('/api/subscriptions/' + subId, editSubState).then(function(res) {
      toast.success('Subscription updated');
      setEditSub(null);
      loadClientSubscriptions();
      loadInvoices();
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    });
  }

  function handlePrepareMissingSubscriptionInvoices() {
    setSubscriptionActionLoading(function(prev) {
      return { ...prev, bulkPrepare: 'prepare' };
    });

    api.post('/api/clients/' + params.id + '/subscriptions/prepare-missing-invoices').then(function(res) {
      if (res.data.created > 0) {
        toast.success('Prepared ' + res.data.created + ' missing recurring invoice' + (res.data.created > 1 ? 's' : ''));
      } else {
        toast.success('No missing recurring invoices to prepare');
      }
      loadInvoices();
      loadClientSubscriptions();
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to prepare recurring invoices');
    }).finally(function() {
      setSubscriptionActionLoading(function(prev) {
        return { ...prev, bulkPrepare: null };
      });
    });
  }


  function handleSetDefaultCard(cardId) {
    api.post('/api/clients/' + params.id + '/cards/' + cardId + '/set-default').then(function() {
      toast.success('Default card updated');
      loadCreditCards();
    }).catch(function() {
      toast.error('Failed to update');
    });
  }

  function handleOpenEditInvoice(invoice) {
    setEditInvoice({
      id: invoice.id,
      invoice_number: invoice.invoice_number,
      status: invoice.status || 'pending',
      due_date: invoice.due_date ? invoice.due_date.split('T')[0] : '',
      notes: invoice.notes || '',
      paid_amount: invoice.paid_amount || 0,
      paid_date: invoice.paid_at ? invoice.paid_at.split('T')[0] : '',
      items: (invoice.items || []).map(function(item) {
        return { name: item.name || '', description: item.description || '', quantity: item.quantity || 1, unit_price: item.unit_price || 0 };
      })
    });
  }

  function handleSaveInvoiceEdit() {
    if (!editInvoice) return;
    setSavingInvoice(true);
    var payload = {
      status: editInvoice.status,
      due_date: editInvoice.due_date || null,
      notes: editInvoice.notes || null,
      paid_amount: parseFloat(editInvoice.paid_amount) || 0,
      paid_date: editInvoice.paid_date || null,
      items: editInvoice.items.map(function(item) {
        return { name: item.name, description: item.description, quantity: parseFloat(item.quantity) || 1, unit_price: parseFloat(item.unit_price) || 0 };
      })
    };
    api.put('/api/invoices/' + editInvoice.id, payload).then(function() {
      toast.success('Invoice ' + editInvoice.invoice_number + ' updated');
      setEditInvoice(null);
      loadInvoices();
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to update invoice');
    }).finally(function() {
      setSavingInvoice(false);
    });
  }

  function updateEditInvoiceField(field, value) {
    setEditInvoice(function(prev) { return { ...prev, [field]: value }; });
  }

  function updateEditInvoiceItem(index, field, value) {
    setEditInvoice(function(prev) {
      var newItems = prev.items.map(function(item, i) {
        if (i === index) { return { ...item, [field]: value }; }
        return item;
      });
      return { ...prev, items: newItems };
    });
  }

  function addEditInvoiceItem() {
    setEditInvoice(function(prev) {
      return { ...prev, items: prev.items.concat([{ name: '', description: '', quantity: 1, unit_price: 0 }]) };
    });
  }

  function removeEditInvoiceItem(index) {
    setEditInvoice(function(prev) {
      return { ...prev, items: prev.items.filter(function(_, i) { return i !== index; }) };
    });
  }

  function handleDeleteLocation(locationId) {
    if (!window.confirm('Delete this location?')) return;
    api.delete('/api/clients/' + params.id + '/locations/' + locationId).then(function() {
      toast.success('Location deleted');
      loadLocations();
    }).catch(function() {
      toast.error('Failed to delete');
    });
  }

  function handleSetPrimaryLocation(locationId) {
    api.post('/api/clients/' + params.id + '/locations/' + locationId + '/set-primary').then(function() {
      toast.success('Primary location updated');
      loadLocations();
    }).catch(function() {
      toast.error('Failed to update');
    });
  }

  function handleSaveLocation(formData) {
    if (editLocation) {
      return api.put('/api/clients/' + params.id + '/locations/' + editLocation.id, formData).then(function() {
        toast.success('Location updated');
        setShowLocationModal(false);
        setEditLocation(null);
        loadLocations();
      }).catch(function() {
        toast.error('Failed to update location');
      });
    } else {
      return api.post('/api/clients/' + params.id + '/locations', formData).then(function() {
        toast.success('Location added');
        setShowLocationModal(false);
        loadLocations();
      }).catch(function() {
        toast.error('Failed to add location');
      });
    }
  }

  function handleDelete() {
    if (!window.confirm('Delete this client?')) return;
    api.delete('/api/clients/' + params.id).then(function() {
      toast.success('Client deleted');
      navigate('/crm/clients');
    }).catch(function() {
      toast.error('Failed to delete');
    });
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  }

  function formatDate(dateStr) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  }

  function getStatusColor(status) {
    var colors = {
      'qualified': 'bg-green-100 text-green-700 border-green-300',
      'new': 'bg-blue-100 text-blue-700 border-blue-300',
      'negotiation': 'bg-purple-100 text-purple-700 border-purple-300',
      'seller': 'bg-orange-100 text-orange-700 border-orange-300',
      'active': 'bg-green-100 text-green-700 border-green-300',
      'inactive': 'bg-gray-100 text-gray-700 border-gray-300',
      'pending': 'bg-yellow-100 text-yellow-700 border-yellow-300'
    };
    return colors[status] || colors['new'];
  }

  function renderTags() {
    if (!client.tags || client.tags.length === 0) return null;
    var tagElements = [];
    for (var i = 0; i < client.tags.length; i++) {
      tagElements.push(
        <span key={client.tags[i]} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm border border-gray-200">
          {client.tags[i]}
        </span>
      );
    }
    return tagElements;
  }

  function renderStatusBadges() {
    var badges = [];
    if (client.status) {
      badges.push(
        <span key="status" className={"px-3 py-1 rounded-md text-xs font-semibold uppercase border " + getStatusColor(client.status)}>
          {client.status}
        </span>
      );
    }
    if (client.tags) {
      for (var i = 0; i < client.tags.length; i++) {
        badges.push(
          <span key={client.tags[i]} className={"px-3 py-1 rounded-md text-xs font-semibold uppercase border " + getStatusColor(client.tags[i])}>
            {client.tags[i]}
          </span>
        );
      }
    }
    return badges;
  }

  // Inline product/service price editing
  var editingItemRef = useState(null); // { index, type, price }
  var editingItem = editingItemRef[0];
  var setEditingItem = editingItemRef[1];

  function handleSaveItemPrice() {
    if (!editingItem) return;
    var type = editingItem.type;
    var idx = editingItem.index;
    var items = type === 'product' ? clientProducts : clientServices;
    var newPrice = parseFloat(editingItem.price) || 0;
    var updated = [];
    for (var i = 0; i < items.length; i++) {
      var copy = {};
      for (var k in items[i]) { copy[k] = items[i][k]; }
      if (i === idx) {
        copy.price = newPrice;
      }
      updated.push(copy);
    }
    var payload = {};
    if (type === 'product') {
      setClientProducts(updated);
      payload.products = updated;
    } else {
      setClientServices(updated);
      payload.services = updated;
    }
    api.put('/api/clients/' + params.id, payload).then(function() {
      toast.success('Price updated to $' + newPrice.toFixed(2));
    }).catch(function() {
      toast.error('Failed to update price');
    });
    setEditingItem(null);
  }

  function renderClientProducts() {
    var elements = [];
    for (var i = 0; i < clientProducts.length; i++) {
      (function(product, index) {
        var billingLabel = { onetime: '1x', monthly: 'Mo', yearly: 'Yr' };
        var billingColor = { 
          onetime: 'bg-purple-100 text-purple-700', 
          monthly: 'bg-blue-100 text-blue-700', 
          yearly: 'bg-green-100 text-green-700' 
        };
        
        elements.push(
          <div key={product.id || index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{product.name}</p>
                <p className="text-sm text-gray-500">{product.description || 'No description'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={"px-2 py-1 rounded text-xs font-medium " + billingColor[product.billing_type]}>
                {billingLabel[product.billing_type]}
              </span>
              {editingItem && editingItem.index === index && editingItem.type === 'product' ? (
                <div className="flex items-center gap-1">
                  <span className="text-sm text-gray-500">$</span>
                  <input type="number" step="0.01" value={editingItem.price} onChange={function(e) { setEditingItem({ index: index, type: 'product', price: e.target.value }); }} onKeyDown={function(e) { if (e.key === 'Enter') handleSaveItemPrice(); if (e.key === 'Escape') setEditingItem(null); }} className="w-24 px-2 py-1 border border-amber-400 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500/30" autoFocus data-testid={'edit-product-price-input-' + index} />
                  <button onClick={function() { handleSaveItemPrice(); }} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
                  <button onClick={function() { setEditingItem(null); }} className="p-1 text-gray-400 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <p className="font-semibold text-gray-900">${(product.price || 0).toFixed(2)}</p>
              )}
              <button 
                onClick={function() { setEditingItem({ index: index, type: 'product', price: product.price || 0 }); }}
                className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                title="Edit Price"
                data-testid={'edit-product-price-' + product.id}
              >
                <Edit className="w-4 h-4" />
              </button>
              <button 
                onClick={function() { handleRemoveProduct(product.id); }}
                className="p-2 text-red-400 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })(clientProducts[i], i);
    }
    return elements;
  }

  function renderClientServices() {
    var elements = [];
    for (var i = 0; i < clientServices.length; i++) {
      (function(service, index) {
        var billingLabel = { onetime: '1x', monthly: 'Mo', yearly: 'Yr' };
        var billingColor = { 
          onetime: 'bg-purple-100 text-purple-700', 
          monthly: 'bg-blue-100 text-blue-700', 
          yearly: 'bg-green-100 text-green-700' 
        };
        
        elements.push(
          <div key={service.id || index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Wrench className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{service.name}</p>
                <p className="text-sm text-gray-500">{service.description || 'No description'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={"px-2 py-1 rounded text-xs font-medium " + billingColor[service.billing_type]}>
                {billingLabel[service.billing_type]}
              </span>
              {editingItem && editingItem.index === index && editingItem.type === 'service' ? (
                <div className="flex items-center gap-1">
                  <span className="text-sm text-gray-500">$</span>
                  <input type="number" step="0.01" value={editingItem.price} onChange={function(e) { setEditingItem({ index: index, type: 'service', price: e.target.value }); }} onKeyDown={function(e) { if (e.key === 'Enter') handleSaveItemPrice(); if (e.key === 'Escape') setEditingItem(null); }} className="w-24 px-2 py-1 border border-amber-400 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-500/30" autoFocus data-testid={'edit-service-price-input-' + index} />
                  <button onClick={function() { handleSaveItemPrice(); }} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
                  <button onClick={function() { setEditingItem(null); }} className="p-1 text-gray-400 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <p className="font-semibold text-gray-900">${(service.price || 0).toFixed(2)}</p>
              )}
              <button 
                onClick={function() { setEditingItem({ index: index, type: 'service', price: service.price || 0 }); }}
                className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                title="Edit Price"
                data-testid={'edit-service-price-' + index}
              >
                <Edit className="w-4 h-4" />
              </button>
              <button 
                onClick={function() { handleRemoveService(service.id); }}
                className="p-2 text-red-400 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })(clientServices[i], i);
    }
    return elements;
  }

  function calculateTotal(billingType) {
    var total = 0;
    for (var i = 0; i < clientProducts.length; i++) {
      if (clientProducts[i].billing_type === billingType) {
        total += clientProducts[i].price || 0;
      }
    }
    for (var j = 0; j < clientServices.length; j++) {
      if (clientServices[j].billing_type === billingType) {
        total += clientServices[j].price || 0;
      }
    }
    return total.toFixed(2);
  }

  function calculateMonthlyTotal() {
    return calculateTotal('monthly');
  }

  function renderLocations() {
    var elements = [];
    for (var i = 0; i < locations.length; i++) {
      (function(location) {
        elements.push(
          <div key={location.id} className="bg-gray-50 rounded-xl border p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className={"w-12 h-12 rounded-xl flex items-center justify-center " + 
                  (location.is_primary ? "bg-amber-100" : "bg-gray-200")}>
                  <MapPin className={"w-6 h-6 " + (location.is_primary ? "text-amber-600" : "text-gray-500")} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-900">{location.name}</h4>
                    {location.is_primary && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                        PRIMARY
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {location.address && (location.address + ', ')}{location.city}{location.state && (', ' + location.state)} {location.zip_code}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    {location.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3.5 h-3.5" />
                        {location.phone}
                      </span>
                    )}
                    {location.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5" />
                        {location.email}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col items-end gap-3">
                {/* Payment Method */}
                <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border">
                  <CreditCard className="w-4 h-4 text-gray-400" />
                  {location.card_last_four ? (
                    <span className="text-sm">
                      <span className="text-gray-400">{location.card_brand || 'Card'}</span> •••• {location.card_last_four}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">No card on file</span>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex gap-1">
                  {!location.is_primary && (
                    <button 
                      onClick={function() { handleSetPrimaryLocation(location.id); }}
                      className="p-2 text-amber-500 hover:bg-amber-50 rounded-lg"
                      title="Set as Primary"
                    >
                      <Star className="w-4 h-4" />
                    </button>
                  )}
                  <button 
                    onClick={function() { setEditLocation(location); setShowLocationModal(true); }}
                    className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={function() { handleDeleteLocation(location.id); }}
                    className="p-2 text-red-400 hover:bg-red-50 rounded-lg"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })(locations[i]);
    }
    return elements;
  }

  function handleAddProduct(product, billingType) {
    var priceKey = 'price_' + billingType;
    var newProduct = {
      id: product.id + '_' + Date.now(),
      product_id: product.id,
      name: product.name,
      description: product.description,
      billing_type: billingType,
      price: product[priceKey]
    };
    var updated = clientProducts.slice();
    updated.push(newProduct);
    setClientProducts(updated);
    
    // Save to backend
    api.put('/api/clients/' + params.id, { products: updated }).then(function() {
      toast.success('Product added');
    });
    setShowAddProductModal(false);
  }

  function handleAddService(service, billingType) {
    var priceKey = 'price_' + billingType;
    var newService = {
      id: service.id + '_' + Date.now(),
      service_id: service.id,
      name: service.name,
      description: service.description,
      billing_type: billingType,
      price: service[priceKey]
    };
    var updated = clientServices.slice();
    updated.push(newService);
    setClientServices(updated);
    
    // Save to backend
    api.put('/api/clients/' + params.id, { services: updated }).then(function() {
      toast.success('Service added');
    });
    setShowAddServiceModal(false);
  }

  function handleRemoveProduct(productId) {
    var updated = [];
    for (var i = 0; i < clientProducts.length; i++) {
      if (clientProducts[i].id !== productId) {
        updated.push(clientProducts[i]);
      }
    }
    setClientProducts(updated);
    api.put('/api/clients/' + params.id, { products: updated }).then(function() {
      toast.success('Product removed');
    });
  }

  function handleRemoveService(serviceId) {
    var updated = [];
    for (var i = 0; i < clientServices.length; i++) {
      if (clientServices[i].id !== serviceId) {
        updated.push(clientServices[i]);
      }
    }
    setClientServices(updated);
    api.put('/api/clients/' + params.id, { services: updated }).then(function() {
      toast.success('Service removed');
    });
  }

  if (loading || !client) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Top Bar */}
      <div className="flex items-center justify-between mb-6">
        <Link to="/crm/clients" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" />
          Back to Contacts
        </Link>
        <div className="flex items-center gap-3">
          <button 
            onClick={function() {
              if (!window.confirm('Revert this client back to a lead? This will delete the client record and reconnect their quote.')) return;
              api.post('/api/clients/' + client.id + '/revert-to-lead').then(function(res) {
                toast.success(res.data.message);
                navigate('/crm/leads/' + res.data.lead_id);
              }).catch(function(err) {
                toast.error(err.response?.data?.detail || 'Failed to revert');
              });
            }}
            className="flex items-center gap-2 px-4 py-2 text-amber-600 hover:bg-amber-50 rounded-lg border border-amber-200"
          >
            <RefreshCw className="w-4 h-4" />
            Revert to Lead
          </button>
          <button 
            onClick={handleDelete}
            className="flex items-center gap-2 px-4 py-2 text-red-500 hover:bg-red-50 rounded-lg border border-red-200"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Left Sidebar */}
        <div className="w-64 flex-shrink-0 space-y-4">
          {/* Profile Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {/* Avatar */}
            <div className="flex justify-center mb-4">
              <div className="w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center">
                <User className="w-12 h-12 text-amber-500" />
              </div>
            </div>
            
            {/* Name & Contact */}
            <div className="text-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">{client.name}</h2>
              <p className="text-sm text-gray-500 flex items-center justify-center gap-1 mt-1">
                <Mail className="w-3.5 h-3.5" />
                {client.email}
              </p>
              {client.phone && (
                <p className="text-sm text-gray-500 flex items-center justify-center gap-1 mt-1">
                  <Phone className="w-3.5 h-3.5" />
                  {client.phone}
                </p>
              )}
            </div>
            
            {/* Status Badges */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {renderStatusBadges()}
            </div>
            
            {/* Action Buttons */}
            <div className="space-y-2">
              <Button 
                className="w-full bg-amber-400 hover:bg-amber-500 text-white"
                onClick={function() { window.location.href = 'tel:' + client.phone; }}
              >
                <Phone className="w-4 h-4 mr-2" />
                Call
              </Button>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={function() { setShowEmailModal(true); }}
              >
                <Mail className="w-4 h-4 mr-2" />
                Email
              </Button>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={isEditingClient ? handleCancelClientEditing : handleUnlockClientEditing}
                data-testid="client-page-edit-toggle-button"
              >
                <Edit className="w-4 h-4 mr-2" />
                {isEditingClient ? 'Cancel Editing' : 'Unlock Page Editing'}
              </Button>
              {isEditingClient && (
                <Button className="w-full bg-[#014DB7] hover:bg-[#013a8c]" onClick={handleSaveClientEditing} data-testid="client-page-save-button">
                  <Check className="w-4 h-4 mr-2" />
                  Save Client Changes
                </Button>
              )}
            </div>
          </div>

          {/* Credit Card Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="flex items-center gap-2 font-semibold text-gray-900">
                <CreditCard className="w-4 h-4 text-amber-500" />
                Credit Cards
              </h3>
              <button 
                onClick={function() { setShowAddCardModal(true); }}
                className="text-xs text-[#014DB7] hover:underline flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
            </div>
            <div className="flex gap-2 mb-3">
              <button onClick={function() {
                api.post('/api/stripe/setup-intent', { client_id: client.id, payment_method: 'cc', origin_url: window.location.origin }).then(function(res) { window.location.href = res.data.url; }).catch(function(err) { toast.error(err.response?.data?.detail || 'Failed'); });
              }} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-[#014DB7] text-white text-xs font-medium rounded-lg hover:bg-[#003A8C]">
                <CreditCard className="w-3 h-3" /> {creditCards.length > 0 ? 'Replace Card via Stripe' : 'Save Card via Stripe'}
              </button>
              <button onClick={function() {
                api.post('/api/stripe/setup-intent', { client_id: client.id, payment_method: 'ach', origin_url: window.location.origin }).then(function(res) { window.location.href = res.data.url; }).catch(function(err) { toast.error(err.response?.data?.detail || 'Failed'); });
              }} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-[#84CC16] text-white text-xs font-medium rounded-lg hover:bg-[#65a30d]">
                {creditCards.length > 0 ? 'Replace Bank (ACH)' : 'Save Bank (ACH)'}
              </button>
            </div>
            <p className="text-[11px] text-blue-600 mb-3">Payment methods cannot be deleted. Add a new verified card or bank to replace the current one.</p>
            
            {creditCards.length > 0 ? (
              <div className="space-y-2">
                {(function() {
                  var cardElements = [];
                  for (var i = 0; i < creditCards.length; i++) {
                    (function(card) {
                      cardElements.push(
                        <div key={card.id} className={"p-3 rounded-lg border " + (card.is_default ? "border-amber-300 bg-amber-50" : "border-gray-200 bg-gray-50")}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={"w-8 h-5 rounded flex items-center justify-center text-white text-xs font-bold " + 
                                (card.brand === 'visa' ? 'bg-blue-600' : 
                                 card.brand === 'mastercard' ? 'bg-red-500' :
                                 card.brand === 'amex' ? 'bg-blue-400' : 'bg-gray-500')}>
                                {card.brand === 'visa' ? 'VISA' : 
                                 card.brand === 'mastercard' ? 'MC' :
                                 card.brand === 'amex' ? 'AMEX' : 'CARD'}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">•••• {card.last_four}</p>
                                <p className="text-xs text-gray-500">Exp: {card.exp_month}/{card.exp_year}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {card.is_default && (
                                <span className="text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded">Default</span>
                              )}
                              {!card.is_default && (
                                <button 
                                  onClick={function() { handleSetDefaultCard(card.id); }}
                                  className="p-1 text-gray-400 hover:text-amber-500"
                                  title="Set as default"
                                >
                                  <Star className="w-3 h-3" />
                                </button>
                              )}
                              <span className="text-[10px] text-blue-600 font-medium">Replace only</span>
                            </div>
                          </div>
                          {card.location_name && (
                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {card.location_name}
                            </p>
                          )}
                        </div>
                      );
                    })(creditCards[i]);
                  }
                  return cardElements;
                })()}
              </div>
            ) : (
              <div className="text-center py-4">
                <CreditCard className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm text-gray-400">No cards on file</p>
                <button 
                  onClick={function() { setShowAddCardModal(true); }}
                  className="text-xs text-[#014DB7] hover:underline mt-2"
                >
                  Add a card
                </button>
              </div>
            )}
          </div>

          {/* Tags Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-4">
              <Tag className="w-4 h-4 text-amber-500" />
              Tags
            </h3>
            <div className="flex flex-wrap gap-2">
              {client.tags && client.tags.length > 0 ? renderTags() : (
                <span className="text-sm text-gray-400">No tags</span>
              )}
            </div>
          </div>

          {/* Set Portal Password Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="admin-set-portal-password">
            <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-4">
              <Lock className="w-4 h-4 text-red-500" />
              Portal Password
            </h3>
            <p className="text-xs text-gray-500 mb-3">Set or reset the client's portal login password.</p>
            <input
              type="text"
              value={portalPassword}
              onChange={function(e) { setPortalPassword(e.target.value); }}
              placeholder="New password (min 6 chars)"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 mb-3"
              data-testid="admin-portal-password-input"
            />
            <button
              onClick={function() {
                if (portalPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
                setSettingPortalPw(true);
                api.post('/api/clients/' + client.id + '/set-portal-password', { password: portalPassword })
                  .then(function(res) { toast.success(res.data.message || 'Portal password set'); setPortalPassword(''); })
                  .catch(function(err) { toast.error(err.response?.data?.detail || 'Failed to set password'); })
                  .finally(function() { setSettingPortalPw(false); });
              }}
              disabled={settingPortalPw || portalPassword.length < 6}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
              data-testid="admin-set-portal-password-btn"
            >
              <Lock className="w-3 h-3" /> {settingPortalPw ? 'Setting...' : 'Set Portal Password'}
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 space-y-6">
          {/* Tabs */}
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-2">
            <div className="flex gap-1">
              <button
                onClick={function() { setActiveTab('overview'); }}
                className={"flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors " + 
                  (activeTab === 'overview' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700')}
              >
                <User className="w-4 h-4" />
                Overview
              </button>
              <button
                onClick={function() { setActiveTab('invoices'); }}
                className={"flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors " + 
                  (activeTab === 'invoices' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700')}
              >
                <Receipt className="w-4 h-4" />
                Invoices ({invoices.length})
              </button>
              <button
                onClick={function() { setActiveTab('locations'); }}
                className={"flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors " + 
                  (activeTab === 'locations' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700')}
              >
                <MapPin className="w-4 h-4" />
                Locations ({locations.length})
              </button>
              <button
                onClick={function() { setActiveTab('products'); }}
                className={"flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors " + 
                  (activeTab === 'products' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700')}
              >
                <Package className="w-4 h-4" />
                Products & Services
              </button>
              <button
                onClick={function() { setActiveTab('documents'); }}
                className={"flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors " + 
                  (activeTab === 'documents' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700')}
                data-testid="client-documents-tab-button"
              >
                <FileText className="w-4 h-4" />
                Documents
              </button>
              <button
                onClick={function() { setActiveTab('notes'); }}
                className={"flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors " + 
                  (activeTab === 'notes' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700')}
              >
                <FileText className="w-4 h-4" />
                Notes ({notes.length})
              </button>
              <button
                onClick={function() { setActiveTab('activity'); }}
                className={"flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors " + 
                  (activeTab === 'activity' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700')}
              >
                <Activity className="w-4 h-4" />
                Activity
              </button>
            </div>
          </div>

          {activeTab === 'overview' && (
            <ClientCarryoverOverview
              client={client}
              draftClient={clientDraft || client}
              setDraftClient={setClientDraft}
              isEditing={isEditingClient}
            />
          )}

          {activeTab === 'invoices' && (
            <div className="space-y-6">
              {clientSubscriptions.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="flex items-center gap-2 font-semibold text-gray-900">
                      <RefreshCw className="w-4 h-4 text-[#014DB7]" />
                      Recurring Charges ({clientSubscriptions.length})
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">These upcoming subscription charges are tied to this client even before a separate invoice is generated.</p>
                  </div>
                  <Button variant="outline" onClick={handlePrepareMissingSubscriptionInvoices} disabled={subscriptionActionLoading.bulkPrepare === 'prepare'} data-testid="prepare-missing-recurring-invoices-button">
                    {subscriptionActionLoading.bulkPrepare === 'prepare' ? 'Preparing…' : 'Prepare Missing Invoices'}
                  </Button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Item</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Billing</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-900 text-sm">Amount</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Next Billing</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-900 text-sm">Status</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-900 text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientSubscriptions.map(function(subscription) {
                        var actionState = subscriptionActionLoading[subscription.id];
                        var existingInvoice = null;
                        for (var ii = 0; ii < invoices.length; ii++) {
                          if (invoices[ii].subscription_id === subscription.id) {
                            existingInvoice = invoices[ii];
                            break;
                          }
                        }
                        return (
                          <tr key={subscription.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4">
                              {editSubState && editSubState._editId === subscription.id ? (
                                <input 
                                  value={editSubState.item_description || ''} 
                                  onChange={function(e) { setEditSub(function(p) { return { ...p, item_description: e.target.value }; }); }}
                                  className="border rounded px-2 py-1 text-sm w-full"
                                  data-testid={'edit-sub-desc-' + subscription.id}
                                />
                              ) : (
                                <div>
                                  <p className="font-medium text-[#0A1628]">{subscription.item_description || 'Recurring item'}</p>
                                  <p className="text-xs text-gray-500">Qty: {subscription.quantity || 1}</p>
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600 capitalize">{subscription.billing_cycle || '-'}</td>
                            <td className="py-3 px-4 text-right">
                              {editSubState && editSubState._editId === subscription.id ? (
                                <input 
                                  type="number" step="0.01" min="0"
                                  value={editSubState.total || ''} 
                                  onChange={function(e) { setEditSub(function(p) { return { ...p, total: e.target.value }; }); }}
                                  className="border rounded px-2 py-1 text-sm w-24 text-right"
                                  data-testid={'edit-sub-amount-' + subscription.id}
                                />
                              ) : (
                                <span className="font-semibold text-gray-900">{formatCurrency(subscription.total)}</span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              {editSubState && editSubState._editId === subscription.id ? (
                                <input 
                                  type="date"
                                  value={editSubState.next_billing_date ? editSubState.next_billing_date.substring(0, 10) : ''} 
                                  onChange={function(e) { setEditSub(function(p) { return { ...p, next_billing_date: e.target.value }; }); }}
                                  className="border rounded px-2 py-1 text-sm"
                                  data-testid={'edit-sub-date-' + subscription.id}
                                />
                              ) : (
                                <span className="text-sm text-gray-600">{subscription.next_billing_date ? new Date(subscription.next_billing_date).toLocaleDateString() : '-'}</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex flex-col items-center gap-1">
                                <span className={'px-2 py-1 rounded-full text-xs font-medium capitalize ' + (subscription.status === 'active' ? 'bg-green-100 text-green-700' : subscription.status === 'paused' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700')}>
                                  {subscription.status}
                                </span>
                                <span className={'px-2 py-0.5 rounded-full text-[11px] font-medium ' + (existingInvoice ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700')}>
                                  {existingInvoice ? ('Invoice ' + existingInvoice.invoice_number) : 'Missing invoice'}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                {editSubState && editSubState._editId === subscription.id ? (
                                  <>
                                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={function() { handleSaveSubscriptionEdit(subscription.id); }} data-testid={'save-sub-edit-' + subscription.id}>
                                      <Check className="w-3 h-3 mr-1" /> Save
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={function() { setEditSub(null); }} data-testid={'cancel-sub-edit-' + subscription.id}>
                                      Cancel
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button variant="outline" size="sm" onClick={function() { 
                                      setEditSub({ 
                                        _editId: subscription.id, 
                                        item_description: subscription.item_description || '', 
                                        total: subscription.total || 0, 
                                        next_billing_date: subscription.next_billing_date || '' 
                                      }); 
                                    }} data-testid={'edit-sub-' + subscription.id}>
                                      <Edit className="w-3 h-3 mr-1" /> Edit
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={function() { handleGenerateSubscriptionInvoice(subscription.id); }} disabled={!!actionState} data-testid={'client-subscription-invoice-' + subscription.id}>
                                      {actionState === 'invoice' ? 'Working...' : 'Invoice'}
                                    </Button>
                                    <Button size="sm" className="bg-[#014DB7] hover:bg-[#013a8c]" onClick={function() { handleChargeSubscriptionNow(subscription.id); }} disabled={!!actionState} data-testid={'client-subscription-charge-' + subscription.id}>
                                      {actionState === 'charge' ? 'Charging...' : 'Charge Now'}
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              )}

              {/* Quotes Section */}
              {clientQuotes.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-4">
                  <FileText className="w-4 h-4 text-[#014DB7]" />
                  Quotes ({clientQuotes.length})
                </h3>
                <div className="space-y-2">
                  {clientQuotes.map(function(q) {
                    return (
                      <div key={q.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                        <div>
                          <p className="font-medium text-[#0A1628]">{q.name}</p>
                          <p className="text-xs text-gray-500">${(q.total || 0).toLocaleString('en-US', {minimumFractionDigits: 2})} {q.signed_at ? '• Signed' : '• Draft'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={'px-2 py-1 rounded-full text-xs font-semibold ' + (q.signed_at ? 'bg-green-100 text-green-700' : q.sent_at ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600')}>{q.signed_at ? 'Signed' : q.sent_at ? 'Sent' : 'Draft'}</span>
                          <button onClick={function() { navigate('/crm/leads/' + (q.lead_id || params.id) + '/quote/' + q.id); }} className="text-xs text-[#014DB7] hover:underline">View</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              )}

              {/* Invoices Header */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="flex items-center gap-2 font-semibold text-gray-900">
                      <Receipt className="w-4 h-4 text-amber-500" />
                      Invoices
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">Billing history and payment tracking</p>
                  </div>
                  <Button onClick={function() { setShowCreateInvoiceModal(true); }} className="bg-[#014DB7]">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Invoice
                  </Button>
                </div>

                {invoices.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Invoice #</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Invoice Date</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Due Date</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Date Paid</th>
                          <th className="text-right py-3 px-4 font-semibold text-gray-900 text-sm">Total</th>
                          <th className="text-right py-3 px-4 font-semibold text-gray-900 text-sm">Paid</th>
                          <th className="text-right py-3 px-4 font-semibold text-gray-900 text-sm">Transaction</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Payment</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Product Info</th>
                          <th className="text-center py-3 px-4 font-semibold text-gray-900 text-sm">Status</th>
                          <th className="text-right py-3 px-4 font-semibold text-gray-900 text-sm">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(function() {
                          var rows = [];
                          for (var i = 0; i < invoices.length; i++) {
                            (function(invoice) {
                              var statusColor = invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                                invoice.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-700';
                              
                              var productNames = (invoice.items || []).map(function(item) { return item.name; }).join(', ');
                              
                              rows.push(
                                <tr key={invoice.id} className="border-b border-gray-100 hover:bg-gray-50">
                                  <td className="py-3 px-4">
                                    <span className="font-mono text-sm font-medium text-[#014DB7]">{invoice.invoice_number}</span>
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600">
                                    {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : '-'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600">
                                    {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '-'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600">
                                    {invoice.paid_at ? new Date(invoice.paid_at).toLocaleDateString() : '-'}
                                  </td>
                                  <td className="py-3 px-4 text-right">
                                    <span className="font-semibold text-gray-900">${(invoice.total || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                    {invoice.processing_fee > 0 && invoice.status === 'paid' && (
                                      <span className="block text-[10px] text-amber-600">+${invoice.processing_fee.toFixed(2)} fee</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4 text-right">
                                    <span className="font-semibold text-green-700" data-testid={'invoice-paid-amount-' + invoice.id}>${(invoice.paid_amount || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                  </td>
                                  <td className="py-3 px-4 text-right">
                                    <span className="font-medium text-gray-900" data-testid={'invoice-transaction-amount-' + invoice.id}>${((invoice.transaction_amount || invoice.paid_amount || 0)).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600">
                                    <span className="capitalize">{invoice.payment_method || invoice.paid_via || '-'}</span>
                                  </td>
                                  <td className="py-3 px-4">
                                    <span className="text-sm text-gray-600 max-w-xs truncate block">{productNames || '-'}</span>
                                  </td>
                                  <td className="py-3 px-4 text-center">
                                    <span className={"px-2 py-1 rounded-full text-xs font-medium capitalize " + statusColor}>
                                      {invoice.status}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      <button 
                                        onClick={function() { handleOpenEditInvoice(invoice); }}
                                        className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                                        title="Edit Invoice"
                                        data-testid={'edit-invoice-' + invoice.id}
                                      >
                                        <Edit className="w-4 h-4" />
                                      </button>
                                      <button 
                                        onClick={function() { navigate('/crm/invoices'); }}
                                        className="p-1.5 text-gray-400 hover:text-[#014DB7] hover:bg-gray-100 rounded"
                                        title="View Invoice"
                                      >
                                        <FileText className="w-4 h-4" />
                                      </button>
                                      <button 
                                        onClick={function() { window.open('/crm/invoice/' + invoice.id + '/print', '_blank'); }}
                                        className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                                        title="Print Invoice"
                                        data-testid={'print-invoice-' + invoice.id}
                                      >
                                        <Receipt className="w-4 h-4" />
                                      </button>
                                      {invoice.status !== 'paid' && (
                                        <button 
                                          onClick={function() {
                                            api.post('/api/invoices/' + invoice.id + '/send').then(function(res) {
                                              toast.success(res.data.message || 'Invoice sent');
                                              loadInvoices();
                                            }).catch(function(err) {
                                              toast.error(err.response?.data?.detail || 'Failed to send invoice');
                                            });
                                          }}
                                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                          title="Send Invoice to Client"
                                          data-testid={'send-invoice-' + invoice.id}
                                        >
                                          <Send className="w-4 h-4" />
                                        </button>
                                      )}
                                      {invoice.status !== 'paid' && creditCards.length > 0 && (
                                        <button 
                                          onClick={function() {
                                            var baseAmt = invoice.total || 0;
                                            var ccRate = 0.029;
                                            var ccFlat = 0.30;
                                            var chargeAmt = Math.round(((baseAmt + ccFlat) / (1 - ccRate)) * 100) / 100;
                                            var feeAmt = Math.round((chargeAmt - baseAmt) * 100) / 100;
                                            if (!window.confirm('Invoice Total: $' + baseAmt.toFixed(2) + '\nProcessing Fee (2.9% + $0.30): $' + feeAmt.toFixed(2) + '\nTotal Charge: $' + chargeAmt.toFixed(2) + '\n\nCharge $' + chargeAmt.toFixed(2) + ' to the client\'s saved card now?')) return;
                                            api.post('/api/invoices/' + invoice.id + '/charge-now').then(function(res) {
                                              toast.success(res.data.message || 'Charged successfully');
                                              loadInvoices();
                                            }).catch(function(err) {
                                              toast.error(err.response?.data?.detail || 'Charge failed');
                                            });
                                          }}
                                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                                          title="Charge Now (Saved Card)"
                                          data-testid={'charge-now-' + invoice.id}
                                        >
                                          <CreditCard className="w-4 h-4" />
                                        </button>
                                      )}
                                      {invoice.status !== 'paid' && (
                                        <button 
                                          onClick={function() {
                                            api.post('/api/invoices/' + invoice.id + '/mark-paid').then(function() {
                                              toast.success('Invoice marked as paid');
                                              loadInvoices();
                                            }).catch(function() {
                                              toast.error('Failed to update invoice');
                                            });
                                          }}
                                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                                          title="Mark as Paid (Manual)"
                                        >
                                          <Check className="w-4 h-4" />
                                        </button>
                                      )}
                                      <button 
                                        onClick={function() {
                                          if (!window.confirm('Delete invoice ' + (invoice.invoice_number || '') + '? This cannot be undone.')) return;
                                          api.delete('/api/invoices/' + invoice.id).then(function() {
                                            toast.success('Invoice deleted');
                                            loadInvoices();
                                          }).catch(function() {
                                            toast.error('Failed to delete invoice');
                                          });
                                        }}
                                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                        title="Delete Invoice"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })(invoices[i]);
                          }
                          return rows;
                        })()}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No invoices yet</p>
                    <p className="text-sm mt-1">Create an invoice to start billing this client</p>
                    <Button onClick={function() { setShowCreateInvoiceModal(true); }} className="mt-4 bg-[#014DB7]">
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Invoice
                    </Button>
                  </div>
                )}
              </div>

              {/* Edit Invoice Modal */}
              {editInvoice && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={function(e) { if (e.target === e.currentTarget) setEditInvoice(null); }}>
                  <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-invoice-modal">
                    <div className="p-6 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold text-[#0A1628] flex items-center gap-2">
                          <Edit className="w-5 h-5 text-amber-500" />
                          Edit Invoice {editInvoice.invoice_number}
                        </h3>
                        <button onClick={function() { setEditInvoice(null); }} className="p-2 hover:bg-gray-100 rounded-full">
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <div className="p-6 space-y-5">
                      {/* Status + Due Date */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                          <select value={editInvoice.status} onChange={function(e) { updateEditInvoiceField('status', e.target.value); }} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400" data-testid="edit-invoice-status">
                            <option value="draft">Draft</option>
                            <option value="pending">Pending</option>
                            <option value="sent">Sent</option>
                            <option value="paid">Paid</option>
                            <option value="overdue">Overdue</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                          <input type="date" value={editInvoice.due_date} onChange={function(e) { updateEditInvoiceField('due_date', e.target.value); }} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400" data-testid="edit-invoice-due-date" />
                        </div>
                      </div>

                      {/* Paid Amount + Paid Date */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Paid Amount ($)</label>
                          <input type="number" step="0.01" value={editInvoice.paid_amount} onChange={function(e) { updateEditInvoiceField('paid_amount', e.target.value); }} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400" data-testid="edit-invoice-paid-amount" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Paid Date</label>
                          <input type="date" value={editInvoice.paid_date} onChange={function(e) { updateEditInvoiceField('paid_date', e.target.value); }} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400" data-testid="edit-invoice-paid-date" />
                        </div>
                      </div>

                      {/* Line Items */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-sm font-semibold text-gray-700">Line Items</label>
                          <button onClick={addEditInvoiceItem} className="text-xs text-[#014DB7] hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Add Item</button>
                        </div>
                        <div className="space-y-3">
                          {editInvoice.items.map(function(item, idx) {
                            return (
                              <div key={idx} className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                                <div className="grid grid-cols-12 gap-3 items-end">
                                  <div className="col-span-5">
                                    <label className="block text-xs text-gray-500 mb-1">Name</label>
                                    <input type="text" value={item.name} onChange={function(e) { updateEditInvoiceItem(idx, 'name', e.target.value); }} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30" placeholder="Item name" />
                                  </div>
                                  <div className="col-span-2">
                                    <label className="block text-xs text-gray-500 mb-1">Qty</label>
                                    <input type="number" step="1" min="1" value={item.quantity} onChange={function(e) { updateEditInvoiceItem(idx, 'quantity', e.target.value); }} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
                                  </div>
                                  <div className="col-span-3">
                                    <label className="block text-xs text-gray-500 mb-1">Unit Price ($)</label>
                                    <input type="number" step="0.01" value={item.unit_price} onChange={function(e) { updateEditInvoiceItem(idx, 'unit_price', e.target.value); }} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30" />
                                  </div>
                                  <div className="col-span-1 text-right text-sm font-semibold text-gray-700 pb-2">
                                    ${((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toFixed(2)}
                                  </div>
                                  <div className="col-span-1 pb-2">
                                    {editInvoice.items.length > 1 && (
                                      <button onClick={function() { removeEditInvoiceItem(idx); }} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                                    )}
                                  </div>
                                </div>
                                <div className="mt-2">
                                  <input type="text" value={item.description} onChange={function(e) { updateEditInvoiceItem(idx, 'description', e.target.value); }} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30" placeholder="Description (optional)" />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-3 text-right">
                          <span className="text-sm text-gray-500">Subtotal: </span>
                          <span className="text-lg font-bold text-gray-900">
                            ${editInvoice.items.reduce(function(sum, item) { return sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)); }, 0).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      {/* Notes */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <textarea value={editInvoice.notes} onChange={function(e) { updateEditInvoiceField('notes', e.target.value); }} rows={3} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 resize-none" placeholder="Internal notes..." data-testid="edit-invoice-notes" />
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
                      <Button variant="outline" onClick={function() { setEditInvoice(null); }}>Cancel</Button>
                      <Button onClick={handleSaveInvoiceEdit} disabled={savingInvoice} className="bg-[#014DB7] hover:bg-[#013a8c]" data-testid="save-invoice-edit-btn">
                        {savingInvoice ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'locations' && (
            <div className="space-y-6">
              {/* Locations Header */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="flex items-center gap-2 font-semibold text-gray-900">
                      <MapPin className="w-4 h-4 text-amber-500" />
                      Locations
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">Manage multiple locations with separate billing</p>
                  </div>
                  <Button onClick={function() { setEditLocation(null); setShowLocationModal(true); }} className="bg-[#014DB7]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Location
                  </Button>
                </div>

                {locations.length > 0 ? (
                  <div className="space-y-4">
                    {renderLocations()}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No locations added</p>
                    <p className="text-sm mt-1">Add locations to manage multi-site billing</p>
                    <Button onClick={function() { setEditLocation(null); setShowLocationModal(true); }} className="mt-4 bg-[#014DB7]">
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Location
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'products' && (
            <div className="space-y-6">
              {/* Price Calculator */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-4">
                  <DollarSign className="w-4 h-4 text-amber-500" />
                  Price Calculator
                </h3>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                    <p className="text-xs text-purple-600 font-medium uppercase mb-1">One-Time Total</p>
                    <p className="text-2xl font-bold text-purple-700">${calculateTotal('onetime')}</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <p className="text-xs text-blue-600 font-medium uppercase mb-1">Monthly Total</p>
                    <p className="text-2xl font-bold text-blue-700">${calculateTotal('monthly')}</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <p className="text-xs text-green-600 font-medium uppercase mb-1">Yearly Total</p>
                    <p className="text-2xl font-bold text-green-700">${calculateTotal('yearly')}</p>
                  </div>
                </div>
              </div>

              {/* Products Section */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="flex items-center gap-2 font-semibold text-gray-900">
                    <Package className="w-4 h-4 text-amber-500" />
                    Products ({clientProducts.length})
                  </h3>
                  <Button onClick={function() { setShowAddProductModal(true); }} className="bg-[#014DB7]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Product
                  </Button>
                </div>

                {clientProducts.length > 0 ? (
                  <div className="space-y-3">
                    {renderClientProducts()}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p>No products assigned</p>
                  </div>
                )}
              </div>

              {/* Services Section */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="flex items-center gap-2 font-semibold text-gray-900">
                    <Wrench className="w-4 h-4 text-amber-500" />
                    Services ({clientServices.length})
                  </h3>
                  <Button onClick={function() { setShowAddServiceModal(true); }} className="bg-[#014DB7]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Service
                  </Button>
                </div>

                {clientServices.length > 0 ? (
                  <div className="space-y-3">
                    {renderClientServices()}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Wrench className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p>No services assigned</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <ClientDocumentsPanel api={api} clientId={params.id} isSuperAdmin={!!authContext.isSuperAdmin} />
          )}

          {activeTab === 'notes' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Notes</h3>
                {isEditingClient && <Button className="bg-[#014DB7]" onClick={handleSaveClientEditing}>Save Notes</Button>}
              </div>
              {isEditingClient ? (
                <Textarea
                  value={(clientDraft && clientDraft.notes) || ''}
                  onChange={function(e) { setClientDraft({ ...(clientDraft || client), notes: e.target.value }); }}
                  rows={8}
                  placeholder="Client notes follow over from the lead and can be edited here."
                  data-testid="client-notes-textarea"
                />
              ) : notes.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No notes yet</p>
                </div>
              ) : (
                <div className="space-y-4 bg-gray-50 rounded-xl p-5">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{client.notes}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Activity Log</h3>
              <ClientActivityTimeline activities={activities} />
            </div>
          )}
        </div>
      </div>

      {/* Add Product Modal */}
      {showAddProductModal && (
        <AddProductModal
          products={availableProducts}
          onClose={function() { setShowAddProductModal(false); }}
          onAdd={handleAddProduct}
        />
      )}

      {/* Add Service Modal */}
      {showAddServiceModal && (
        <AddServiceModal
          services={availableServices}
          onClose={function() { setShowAddServiceModal(false); }}
          onAdd={handleAddService}
        />
      )}

      {/* Location Modal */}
      {showLocationModal && (
        <LocationModal
          location={editLocation}
          onClose={function() { setShowLocationModal(false); setEditLocation(null); }}
          onSave={handleSaveLocation}
        />
      )}

      {/* Add Card Modal */}
      {showAddCardModal && (
        <AddCardModal
          locations={locations}
          onClose={function() { setShowAddCardModal(false); }}
          onSave={function(cardData) {
            api.post('/api/clients/' + params.id + '/cards', cardData).then(function() {
              toast.success('Card added');
              setShowAddCardModal(false);
              loadCreditCards();
            }).catch(function(err) {
              toast.error(err.response?.data?.detail || 'Failed to add card');
            });
          }}
        />
      )}

      {/* Create Invoice Modal */}
      {showCreateInvoiceModal && (
        <CreateInvoiceModal
          client={client}
          products={availableProducts}
          services={availableServices}
          leadSales={leadSales}
          locations={locations}
          onClose={function() { setShowCreateInvoiceModal(false); }}
          onSave={function(invoiceData) {
            api.post('/api/invoices', invoiceData).then(function() {
              toast.success('Invoice created');
              setShowCreateInvoiceModal(false);
              loadInvoices();
            }).catch(function(err) {
              toast.error(err.response?.data?.detail || 'Failed to create invoice');
            });
          }}
        />
      )}

      <EmailComposerModal
        isOpen={showEmailModal}
        onClose={function() { setShowEmailModal(false); }}
        recipientEmail={client?.email}
        recipientName={client?.company_name || client?.first_name + ' ' + client?.last_name}
        clientId={client?.id}
      />
    </div>
  );
}

export default ClientDetailPage;
